/**
 * 发票列表列定义（多个工作台共用，保证同一张发票在哪个页面看都长一样）
 *
 * 财务场景细节：
 *   - 金额右对齐、等宽、千分位；
 *   - 发票号码/代码等长数字用等宽字体，避免 0/O、1/l 看错；
 *   - 风险列同时有颜色与文字标签。
 */

import { Link } from 'react-router-dom';
import { Space, Tag, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { INVOICE_TYPE_LABELS, PermissionCode, formatTaxDate, type InvoiceListItem } from '@ilc/shared';
import { InvoiceStatusTag } from './InvoiceStatusTag';
import { RiskTag } from './RiskTag';
import { Money } from './Money';
import { MaskedField } from './MaskedField';
import type { ExportColumn } from '@/utils/export';
import { formatCents } from '@ilc/shared';

export interface InvoiceColumnOptions {
  /** 展示经办人列（经办人自己的工作台可以隐藏） */
  showHandler?: boolean;
  /** 展示「当前节点」列 */
  showNode?: boolean;
  /** 展示 T+ 凭证号列 */
  showVoucher?: boolean;
  /** 展示销方税号（按字段权限脱敏） */
  showSellerTaxNo?: boolean;
  /** 行操作区 */
  actions?: (record: InvoiceListItem) => React.ReactNode;
  /** 点击单号跳转的路径前缀，默认 /business/invoices */
  detailPathPrefix?: string;
}

export function buildInvoiceColumns(options: InvoiceColumnOptions = {}): ColumnsType<InvoiceListItem> {
  const {
    showHandler = false,
    showNode = true,
    showVoucher = false,
    showSellerTaxNo = true,
    actions,
    detailPathPrefix = '/business/invoices',
  } = options;

  const columns: ColumnsType<InvoiceListItem> = [
    {
      title: '内部单号',
      dataIndex: 'bizNo',
      key: 'bizNo',
      width: 168,
      fixed: 'left',
      render: (value: string, record) => (
        <Link to={`${detailPathPrefix}/${record.id}`} className="ilc-mono" style={{ fontSize: 12 }}>
          {value}
        </Link>
      ),
    },
    {
      title: '发票号码',
      dataIndex: 'invoiceNo',
      key: 'invoiceNo',
      width: 190,
      render: (value: string, record) => (
        <Space direction="vertical" size={0}>
          <span className="ilc-mono" style={{ fontSize: 12 }}>
            {value}
          </span>
          <Typography.Text type="secondary" style={{ fontSize: 11 }} className="ilc-mono">
            {record.invoiceCode ? `代码 ${record.invoiceCode}` : '数电票（无代码）'}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: '票种',
      dataIndex: 'invoiceType',
      key: 'invoiceType',
      width: 150,
      render: (value: InvoiceListItem['invoiceType']) => (
        <Tooltip title={`票种代码 ${value}`}>
          <Tag style={{ whiteSpace: 'normal' }}>{INVOICE_TYPE_LABELS[value] ?? value}</Tag>
        </Tooltip>
      ),
    },
    {
      title: '销方名称',
      dataIndex: 'sellerName',
      key: 'sellerName',
      width: 220,
      ellipsis: true,
      render: (value: string | null) => value ?? '-',
    },
  ];

  if (showSellerTaxNo) {
    columns.push({
      title: '销方税号',
      dataIndex: 'sellerTaxNo',
      key: 'sellerTaxNo',
      width: 190,
      render: (value: string | null) => (
        <MaskedField value={value} kind="taxNo" permission={PermissionCode.INVOICE_READ} />
      ),
    });
  }

  columns.push(
    {
      title: '价税合计',
      dataIndex: 'totalCents',
      key: 'totalCents',
      width: 150,
      align: 'right',
      sorter: true,
      render: (value: number | null) => <Money cents={value} withChinese strong />,
    },
    {
      title: '开票日期',
      dataIndex: 'invoiceDate',
      key: 'invoiceDate',
      width: 120,
      sorter: true,
      render: (value: string | null) => <span className="ilc-mono">{formatTaxDate(value) || '-'}</span>,
    },
    {
      title: '关联合同',
      dataIndex: 'contractNo',
      key: 'contractNo',
      width: 150,
      render: (value: string | null) =>
        value ? (
          <span className="ilc-mono" style={{ fontSize: 12 }}>
            {value}
          </span>
        ) : (
          <Tooltip title="未关联合同：低风险提示，建议补充关联以便审计追溯">
            <Tag color="default">未关联</Tag>
          </Tooltip>
        ),
    },
    {
      title: '状态',
      dataIndex: 'invoiceStatus',
      key: 'invoiceStatus',
      width: 120,
      render: (value: InvoiceListItem['invoiceStatus']) => <InvoiceStatusTag status={value} />,
    },
    {
      title: '风险',
      dataIndex: 'riskLevel',
      key: 'riskLevel',
      width: 110,
      render: (value: InvoiceListItem['riskLevel'], record) => (
        <Tooltip title={record.riskItems.map((i) => i.message).join('\n') || undefined}>
          <span>
            <RiskTag level={value} />
          </span>
        </Tooltip>
      ),
    },
  );

  if (showNode) {
    columns.push({
      title: '当前节点 / 待办人',
      dataIndex: 'currentNodeName',
      key: 'currentNodeName',
      width: 190,
      render: (_value: unknown, record) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontSize: 12 }}>{record.currentNodeName ?? '-'}</span>
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            {record.pendingRoleName ?? ''}
          </Typography.Text>
        </Space>
      ),
    });
  }

  if (showHandler) {
    columns.push({
      title: '经办人',
      dataIndex: 'handlerName',
      key: 'handlerName',
      width: 100,
      render: (value: string | null) => value ?? '-',
    });
  }

  if (showVoucher) {
    columns.push({
      title: 'T+ 凭证号',
      key: 'voucherNo',
      width: 170,
      render: (_value: unknown, record) => {
        /* 用列表下发的 tplusVoucherNo，**不是** record.tplus?.voucherNo——
         * 列表接口不返回 tplus（那是详情才查的完整同步记录），
         * 从前这里读它，于是这一列无论后台有没有回写都是空的 */
        const voucherNo = record.tplusVoucherNo ?? null;
        if (!voucherNo) {
          return (
            <Tooltip title="凭证由财务人员在 T+ 端选择会计科目后生成，本平台只接收凭证号回写">
              <Tag color="default">待 T+ 端生成</Tag>
            </Tooltip>
          );
        }
        return (
          <span className="ilc-mono" style={{ fontSize: 12 }}>
            {voucherNo}
          </span>
        );
      },
    });
  }

  if (actions) {
    columns.push({
      title: '操作',
      key: 'actions',
      /* 加宽到 280：动作已增到 6 个（详情/提交校验/处理退回/关联合同/上传附件/工程名称），
       * 220 下会换行挤成一团 */
      width: 280,
      fixed: 'right',
      render: (_value: unknown, record) => <Space size={4}>{actions(record)}</Space>,
    });
  }

  return columns;
}

/**
 * 导出列（CSV）—— 金额导出为元的数字字符串，便于 Excel 直接求和。
 *
 * 【为什么数值列要自己处理 null，而不是交给 formatCents】
 * `formatCents(null)` 返回 `'-'`。那一列在 Excel 里就变成了**字符串**，
 * 整列求和会得到 0 或报错。导出留空才是对的——空单元格不参与求和，
 * 也不会被误读成"金额是 0"。
 *
 * 【列名的叫法】一律用「工程名称」。本仓库里「项目名称」已被发票明细表的
 * 「开票项目名」占用（如 `*信息技术服务*软件开发费`），共用会让审核人误读。
 */
export function buildInvoiceExportColumns(): ExportColumn<InvoiceListItem>[] {
  return [
    { title: '内部单号', dataIndex: 'bizNo' },
    { title: '发票代码', dataIndex: 'invoiceCode' },
    { title: '发票号码', dataIndex: 'invoiceNo' },
    { title: '票种', value: (r) => INVOICE_TYPE_LABELS[r.invoiceType] ?? r.invoiceType },
    { title: '销方名称', dataIndex: 'sellerName' },
    { title: '工程名称', value: (r) => r.projectName ?? '' },
    // 金额三件套按**票面阅读顺序**排：不含税金额 → 税率 → 税额 → 价税合计
    { title: '金额(元)', value: (r) => (r.amountCents === null ? '' : formatCents(r.amountCents)) },
    { title: '税率', value: (r) => r.taxRateText ?? '' },
    { title: '税额(元)', value: (r) => (r.taxCents === null ? '' : formatCents(r.taxCents)) },
    { title: '价税合计(元)', value: (r) => (r.totalCents === null ? '' : formatCents(r.totalCents)) },
    { title: '开票日期', value: (r) => formatTaxDate(r.invoiceDate) },
    { title: '关联合同', dataIndex: 'contractNo' },
    { title: '状态', value: (r) => r.invoiceStatus },
    { title: '风险等级', value: (r) => r.riskLevel },
    { title: '当前节点', dataIndex: 'currentNodeName' },
    { title: '经办人', value: (r) => r.handlerName ?? '' },
    { title: 'T+凭证号', value: (r) => r.tplusVoucherNo ?? '' },
  ];
}

/* ==========================================================================
 * 专用报表
 * ========================================================================== */

/** 报表的一行。「合计」行由 buildInvoiceReportRows 在末尾追加 */
export interface InvoiceReportRow {
  /** 序号；合计行为空串 */
  seq: number | '';
  unitName: string;
  invoiceDate: string;
  invoiceNo: string;
  /** 材料金额（不含税），按「分」存 */
  materialCents: number | '';
  /** 进项税额 */
  taxCents: number | '';
  /** 价税合计 */
  totalCents: number | '';
  remarks: string;
}

/**
 * 专用报表的列。
 *
 * 【与「导出本页」的区别】那个导的是列表所见即所得（含状态/风险/节点等运维字段）；
 * 这个报表是交给财务与审计的**成品**：只留对账要用的八列，末尾带合计行。
 * 两张表服务两种场景，不要合并——列表字段一改就会污染报表。
 */
export function buildInvoiceReportColumns(): ExportColumn<InvoiceReportRow>[] {
  const money = (v: number | '') => (v === '' ? '' : formatCents(v));
  return [
    { title: '序号', value: (r) => r.seq },
    { title: '单位名称', dataIndex: 'unitName' },
    { title: '发票日期', dataIndex: 'invoiceDate' },
    { title: '票号', dataIndex: 'invoiceNo' },
    { title: '材料金额', value: (r) => money(r.materialCents) },
    { title: '进项税额', value: (r) => money(r.taxCents) },
    { title: '合计', value: (r) => money(r.totalCents) },
    { title: '备注', dataIndex: 'remarks' },
  ];
}

/**
 * 把发票列表转成报表行，**末尾追加合计行**。
 *
 * 【金额缺失怎么算】发票的金额可能尚未识别出来（`amountCents` 为 null）。
 * 这样的行在报表里**留空而不是填 0**——填 0 会让合计看起来是"对得上的"，
 * 而实际上漏了一张票的金额；留空则在视觉上提示"这行还没数"。
 * 合计只累加有值的行，并在表尾另有说明的责任交给使用者（报表本身不擅自补数）。
 */
export function buildInvoiceReportRows(invoices: readonly InvoiceListItem[]): InvoiceReportRow[] {
  const rows: InvoiceReportRow[] = invoices.map((inv, i) => ({
    seq: i + 1,
    unitName: inv.sellerName ?? '',
    invoiceDate: formatTaxDate(inv.invoiceDate) || '',
    invoiceNo: inv.invoiceNo,
    materialCents: inv.amountCents ?? '',
    taxCents: inv.taxCents ?? '',
    totalCents: inv.totalCents ?? '',
    /**
     * 备注里的换行拍平成「；」。
     *
     * 【为什么】票面备注是分行的（地址一行、开户行一行、工程名称一行）。
     * 原样导出时 CSV 会用引号把整个单元格包起来——**格式上完全正确**，
     * 但单元格跨行是 CSV 互操作的头号坑：Excel 能显示，而绝大多数
     * 简易解析器（含财务那边可能用的导入脚本）会从这里开始错行。
     * 报表是要交出去的成品，扁平的一行远比"忠实保留换行"重要；
     * 分句语义用「；」保留。**注意「导出本页」不做这个处理**——
     * 那份是给自己二次核对用的，保持原样。
     */
    remarks: (inv.remarks ?? '').replace(/\s*\n\s*/g, '；'),
  }));

  const sum = (pick: (r: InvoiceReportRow) => number | '') =>
    rows.reduce((n, r) => n + (typeof pick(r) === 'number' ? (pick(r) as number) : 0), 0);

  rows.push({
    seq: '',
    // 「合计」标在单位名称列，与会计手工做表的写法一致
    unitName: '合计',
    invoiceDate: '',
    invoiceNo: '',
    materialCents: sum((r) => r.materialCents),
    taxCents: sum((r) => r.taxCents),
    totalCents: sum((r) => r.totalCents),
    remarks: '',
  });

  return rows;
}
